
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Users, Book, Settings, GraduationCap, CalendarClock, FolderGit2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const AdminDashboard = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect if not an admin
    if (!isAdmin()) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // Fetch counts for dashboard cards
  const { data: usersCount } = useQuery({
    queryKey: ['admin-users-count'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true });
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error('Error fetching users count:', error);
        return '...';
      }
    }
  });

  const { data: modulesCount } = useQuery({
    queryKey: ['admin-modules-count'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('learning_modules')
          .select('*', { count: 'exact', head: true });
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error('Error fetching modules count:', error);
        return '...';
      }
    }
  });

  const { data: mentorsCount } = useQuery({
    queryKey: ['admin-mentors-count'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('user_roles')
          .select('*', { count: 'exact', head: true })
          .eq('role', 'mentor');
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error('Error fetching mentors count:', error);
        return '...';
      }
    }
  });

  const { data: projectsCount } = useQuery({
    queryKey: ['admin-projects-count'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true });
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error('Error fetching projects count:', error);
        return '...';
      }
    }
  });

  const { data: officeHoursCount } = useQuery({
    queryKey: ['admin-office-hours-count'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('office_hours')
          .select('*', { count: 'exact', head: true });
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error('Error fetching office hours count:', error);
        return '...';
      }
    }
  });

  const adminCards = [
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: <Users className="h-8 w-8 text-brand-indigo dark:text-brand-indigo-light" />,
      action: () => navigate('/admin/users'),
      count: usersCount
    },
    {
      title: 'Course Builder',
      description: 'Create and edit learning modules and lessons',
      icon: <Book className="h-8 w-8 text-green-500 dark:text-green-400" />,
      action: () => navigate('/admin/course-builder'),
      count: modulesCount
    },
    {
      title: 'Mentor Management',
      description: 'Manage mentors and office hours',
      icon: <GraduationCap className="h-8 w-8 text-blue-500 dark:text-blue-400" />,
      action: () => navigate('/admin/mentors'),
      count: mentorsCount
    },
    {
      title: 'Project Management',
      description: 'View and manage all student projects',
      icon: <FolderGit2 className="h-8 w-8 text-amber-500 dark:text-amber-400" />,
      action: () => navigate('/admin/projects'),
      count: projectsCount
    },
    {
      title: 'Office Hours',
      description: 'View and manage scheduled office hours',
      icon: <CalendarClock className="h-8 w-8 text-purple-500 dark:text-purple-400" />,
      action: () => navigate('/admin/office-hours'),
      count: officeHoursCount
    },
    {
      title: 'System Settings',
      description: 'Configure system settings and preferences',
      icon: <Settings className="h-8 w-8 text-gray-500 dark:text-gray-400" />,
      action: () => navigate('/admin/settings'),
      count: '...'
    }
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold section-header">Admin Dashboard</h1>
        <p className="text-muted-foreground section-subheader">Manage your bootcamp platform</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminCards.map((card, index) => (
          <Card key={index} className="overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-xl font-bold card-title">{card.title}</CardTitle>
                {card.icon}
              </div>
              <CardDescription>{card.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold dark:text-white">{card.count}</div>
            </CardContent>
            <CardFooter className="bg-muted/50 pt-2 dark:bg-muted/20">
              <Button 
                variant="outline" 
                className="w-full dark:border-gray-700 dark:hover:bg-gray-800" 
                onClick={card.action}
              >
                Manage
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
